/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react'
import { isDesktop, isTablet } from 'react-device-detect'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { EmployeeForm } from '../../components/EmployeeForm/EmployeeForm'
import Loader from '../../components/Loader/Loader'
import { useGetAllTeamsQuery } from '../../redux/team.endpoints'
import { useGetAllTribesQuery } from '../../redux/tribe.endpoints'
import { useCreateUserMutation } from '../../redux/user.endpoints'
import { CreateUserDto } from '../../types/User'
import { BackendError } from '../../types/Errors'
import {
  useLazyGetAllExperienceLevelQuery,
  useLazyGetAllTechnologiesQuery,
} from '../../redux/techstack.endpoints'

interface Props {
  companyId: number
}

const AddEmployee = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const mainRef = useRef<HTMLElement>(null)
  const [createUser, createUserResult] = useCreateUserMutation()
  const { data: tribes, isSuccess: tribesSuccess } = useGetAllTribesQuery({
    companyId,
    pageSize: 0,
    pageNumber: 0,
    sortBy: 'id',
    searchQuery: '',
    sortType: 'DESC',
  })
  const { data: teams, isSuccess: teamsSuccess } =
    useGetAllTeamsQuery(companyId)
  const [getAllExperienceLevel, getAllExperienceLevelResponse] =
    useLazyGetAllExperienceLevelQuery()
  const [getAllTechnologies, getAllTechnologiesResponse] =
    useLazyGetAllTechnologiesQuery()

  useEffect(() => {
    getAllExperienceLevel({
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      sortType: 'DESC',
      search: '',
    })
    getAllTechnologies({
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      sortType: 'DESC',
      search: '',
    })
  }, [])

  useEffect(() => {
    if (createUserResult && createUserResult.isError) {
      const createUserError = createUserResult.error as BackendError
      if (createUserError.data.error) {
        toast.error(createUserError.data.error, {
          toastId: 'create-user-error',
        })
      } else {
        toast.error(`An error occurred when creating member.`, {
          toastId: 'create-user-error',
        })
      }
    } else if (createUserResult && createUserResult.isSuccess) {
      toast.success(`Member created successfully!`, {
        toastId: 'create-user-success',
      })
      navigate('/members')
    }
  }, [createUserResult])

  const handleCreateUser = async (args: CreateUserDto) => {
    const result = await createUser(args)
    if (!('error' in result)) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }

  return (
    <>
      <Helmet>
        <title>Time Off - New Team Member</title>
      </Helmet>
      <main ref={mainRef} className="container-fluid scrollable">
        <div className={`row pt-3 ${isDesktop || isTablet ? 'h-100' : ''}`}>
          <div className="col-8 offset-2 d-flex flex-column align-items-center justify-content-center bg-lazuli-white pb-5">
            <p className="align-self-start font-30 fw-bold">New Team Member</p>
            {tribesSuccess && teamsSuccess ? (
              <EmployeeForm
                handleCreateSubmit={handleCreateUser}
                displayErrorBanner={createUserResult && createUserResult.isError}
                loading={createUserResult && createUserResult.isLoading}
                companyId={companyId}
                tribes={tribes || []}
                teams={teams || []}
                engagements={[]}
                technologyList={getAllTechnologiesResponse.data || []}
                experienceLevel={getAllExperienceLevelResponse.data || []}
                selectedTechStackListByUser={[]}
              />
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </main>
    </>
  )
}

export default AddEmployee
